/**
 * SOLEA cart upsell behaviour.
 *
 * This lives in a theme asset rather than inline in cart-summary.liquid on
 * purpose. Horizon re-renders cart sections by morphing HTML into the DOM, and
 * inline <script> tags in morphed markup are never executed by the browser. An
 * inline script therefore only ran on a full page load, so the buttons were dead
 * inside the cart drawer. Loading once globally and delegating from `document`
 * means the handler survives every re-render.
 *
 * Two rules shape the implementation:
 *
 * 1. Quantity changes are handed to Horizon's own cart-items-component, so the
 *    theme's normal morph path runs and the totals plus the free-shipping bar
 *    stay in step. A hand-rolled fetch would leave them stale.
 * 2. Switching a line to a subscription is not something /cart/change.js can do,
 *    so we clear and re-add. That is only safe because Solea sells a single
 *    product: there is nothing else in the cart to lose. Revisit if that changes.
 *
 * It also reconciles selling plan against quantity. Solea prices subscriptions
 * with two plan groups on one variant: 10% off for 1-2 bags, 19% off for 3+ (the
 * pack saving and the subscribe saving compounded). Shopify binds a selling plan
 * to the cart LINE and has no notion of "this plan needs qty >= 3", so changing
 * the quantity afterwards leaves the wrong plan attached and the wrong price --
 * in both directions. Nothing on the platform re-validates it, so we do.
 */
(() => {
  if (window.soleaUpsellBound) return;
  window.soleaUpsellBound = true;

  const PACK_QTY = 3;

  // Captured before anything else so the refresh path can bypass our own fetch
  // patch below (declared with const, so it must exist before first use).
  const nativeFetch = window.fetch.bind(window);

  /**
   * Re-render the cart in place instead of reloading the page.
   *
   * A full reload tears down the cart drawer. On desktop that reads as a flicker;
   * on mobile the drawer vanishes and the action looks like it failed. Horizon
   * already knows how to redraw its own cart sections, so ask it to, and only fall
   * back to a reload when the component is absent (e.g. a theme update renames it).
   *
   * `bubbles: true` matters -- the components listen on `document`, and an event
   * dispatched on `document` with no bubbling never reaches delegated listeners in
   * some Horizon builds.
   */
  const refreshCart = async () => {
    const component = document.querySelector('cart-items-component');

    // Snapshot what the cart currently renders so we can tell whether a strategy
    // actually redrew it. Without this, a silent no-op would count as success and
    // leave the drawer showing stale prices -- worse than reloading the page.
    const snapshot = () =>
      document.querySelector('cart-items-component')?.textContent?.replace(/\s+/g, ' ').trim() || '';
    const before = snapshot();
    const changed = async (waitMs) => {
      const deadline = Date.now() + waitMs;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 80));
        if (snapshot() !== before) return true;
      }
      return false;
    };

    // Preferred: the component's own refresh, whatever this build calls it.
    for (const method of ['refresh', 'render', 'update']) {
      if (component && typeof component[method] === 'function') {
        try {
          await component[method]();
          if (await changed(1200)) return true;
        } catch (error) {
          /* fall through to the next strategy */
        }
      }
    }

    // Next best: the standard cart event Horizon's own components emit. This is
    // what the theme's quantity stepper relies on, so if it works there it works
    // here.
    try {
      const cart = await nativeFetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) =>
        r.ok ? r.json() : null
      );
      for (const name of ['cart:update', 'cart-lines:update', 'CartUpdateEvent']) {
        document.dispatchEvent(
          new CustomEvent(name, {
            bubbles: true,
            detail: { cart, source: 'solea-upsell', action: 'change' },
          })
        );
      }
      if (await changed(1200)) return true;
    } catch (error) {
      /* fall through to the section-rendering path */
    }

    // Last resort: ask Shopify for the cart section markup and swap it in. Section
    // Rendering returns exactly what the theme would have produced, so line badges,
    // totals and the free-shipping bar all stay in step.
    try {
      const sectionId =
        document.querySelector('cart-items-component')?.dataset.sectionId || 'cart-drawer-section';
      const response = await nativeFetch(`${window.location.pathname}?section_id=${sectionId}`, {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return false;
      const incoming = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('cart-items-component');
      const current = document.querySelector('cart-items-component');
      if (!incoming || !current) return false;
      current.innerHTML = incoming.innerHTML;
      return true;
    } catch (error) {
      return false;
    }
  };

  /**
   * Keep the cart drawer open across a refresh.
   *
   * Horizon's <theme-drawer> owns the dialog lifecycle, so re-opening means asking
   * it rather than touching the <dialog> directly -- otherwise focus trapping and
   * scroll lock are left in an inconsistent state.
   */
  const reopenCartDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (!drawer) return;
    try {
      if (typeof drawer.open === 'function' && !drawer.hasAttribute('open')) drawer.open();
    } catch (error) {
      /* no-op */
    }
  };

  /**
   * Read the plan IDs from wherever they are available. The upsell wrapper is
   * hidden for a subscribed pack cart -- exactly the state we most need to check --
   * so fall back to the cart root, which is always rendered.
   */
  const planIds = () => {
    const source =
      document.querySelector('[data-solea-plans]') || document.querySelector('[data-solea-upsell]');
    if (!source) return null;
    const single = source.dataset.planSingle;
    const pack = source.dataset.planPack;
    if (!single || !pack || single === pack) return null;
    return { single, pack };
  };

  /**
   * Every selling plan with its price tier and delivery interval.
   *
   * Each tier holds one plan per frequency and they all share the same per-delivery
   * price, so a tier id alone cannot identify a plan. Moving a line between tiers
   * therefore has to match on interval as well, or the customer's chosen frequency is
   * lost.
   *
   * The frequency key is the plan's own option value / name (e.g. "deliver every 3
   * months, 10% off"), because Liquid does not expose `delivery_policy` on a selling
   * plan allocation -- that path renders empty. The cadence words are compared rather
   * than the whole string, so the differing discount suffix does not defeat the match.
   */
  const planMap = () => {
    const source =
      document.querySelector('[data-solea-plans]') || document.querySelector('[data-solea-upsell]');
    const raw = source?.dataset.planMap;
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  /**
   * Reduce a plan's label to just its cadence, so "deliver every 3 months, 10% off"
   * and "deliver every 3 months, 19% off" are recognised as the same frequency.
   * Returns an empty string when no cadence can be read, which callers treat as
   * "refuse to act" rather than "matches anything".
   */
  const cadence = (plan) => {
    const raw = String(plan?.freq || '').toLowerCase();
    // Strip the discount suffix ("..., 10% off") and any surrounding punctuation.
    const match = raw.match(/every\s+(\d+\s+)?(day|week|month|year)s?/);
    if (match) return `${(match[1] || '1').trim()} ${match[2]}`;
    return '';
  };

  let reconciling = false;

  /**
   * Ensure every subscription line sits in the price tier its quantity earns, while
   * keeping the delivery frequency the customer chose.
   *
   * Runs after any cart mutation. Guarded against re-entry because the correction
   * itself changes the cart, which would otherwise retrigger this immediately.
   *
   * An earlier version compared each line against one id per tier and forced a match.
   * Since that id always resolved to the monthly plan, choosing any other frequency
   * was silently rewritten to monthly. Frequency is now preserved, and when the target
   * tier has no plan at the same frequency the line is left alone: a suboptimal price
   * is recoverable, a wrong billing frequency is not.
   */
  const reconcilePlans = async () => {
    if (reconciling) return;

    const plans = planIds();
    if (!plans) return;
    const map = planMap();

    reconciling = true;
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();

      // Walk from the end so correcting one line cannot shift the index of a
      // line still to be checked.
      for (let index = cart.items.length - 1; index >= 0; index -= 1) {
        const item = cart.items[index];
        const currentPlan = item.selling_plan_allocation?.selling_plan?.id;
        if (!currentPlan) continue; // one-time line, nothing to reconcile

        const wantTier = item.quantity >= PACK_QTY ? 'pack' : 'single';
        let expected;

        if (map) {
          const current = map.find((plan) => String(plan.id) === String(currentPlan));
          // Right tier already: leave it, whatever the frequency.
          if (current && current.tier === wantTier) continue;
          // Unknown plan (e.g. created after this markup was cached): do not guess.
          if (!current) continue;

          // Without a readable cadence every plan in the tier looks identical, and
          // picking one would silently rewrite the customer's delivery schedule.
          // Leaving the line alone costs at most a suboptimal price.
          const want = cadence(current);
          if (!want) continue;

          const match = map.find(
            (plan) => plan.tier === wantTier && cadence(plan) === want
          );
          // No same-frequency plan in the target tier: leave the line untouched.
          if (!match) continue;
          expected = match.id;
        } else {
          // No map available. Only correct a line that is on the other tier's default
          // plan, so a deliberate non-monthly choice is never overwritten.
          const otherDefault = wantTier === 'pack' ? plans.single : plans.pack;
          if (String(currentPlan) !== String(otherDefault)) continue;
          expected = wantTier === 'pack' ? plans.pack : plans.single;
        }

        if (String(currentPlan) === String(expected)) continue;

        const fix = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: index + 1,
            quantity: item.quantity,
            selling_plan: expected,
          }),
        });

        if (fix.ok) {
          track('plan_corrected', {
            bags: item.quantity,
            from_plan: String(currentPlan),
            to_plan: String(expected),
          });
          // The cart now shows a stale price, so let the theme redraw it.
          reconciling = false;
          if (!(await refreshCart())) window.location.reload();
          else reopenCartDrawer();
          return;
        }
      }
    } catch (error) {
      /* Never let reconciliation break the cart. */
    } finally {
      reconciling = false;
    }
  };

  /**
   * Fire an analytics event for an upsell interaction.
   *
   * Shopify has no native "upsell clicked" metric, so we emit our own to every
   * listener that is already on the page: Shopify's own analytics pipeline
   * (visible in Customer events), GA4 if present, and Klaviyo if present. Nothing
   * here throws if a destination is absent, so this stays safe as tools change.
   */
  const track = (action, detail) => {
    const payload = { action, ...detail };

    try {
      // Shopify custom pixel / Customer events
      window.Shopify?.analytics?.publish?.('solea_upsell', payload);
    } catch (error) {
      /* analytics must never block the cart */
    }

    try {
      // GA4 (also picked up by Google Tag Manager)
      window.gtag?.('event', 'solea_upsell', payload);
      window.dataLayer?.push?.({ event: 'solea_upsell', ...payload });
    } catch (error) {
      /* no-op */
    }

    try {
      // Klaviyo onsite tracking
      window.klaviyo?.push?.(['track', 'Upsell Clicked', payload]);
    } catch (error) {
      /* no-op */
    }

    // Always dispatch a DOM event so anything else can subscribe later.
    document.dispatchEvent(new CustomEvent('solea:upsell', { detail: payload }));
  };

  const setBusy = (el, busy, message) => {
    const box = el.closest('[data-solea-upsell]');
    if (!box) return;
    box.dataset.busy = busy ? 'true' : 'false';
    const status = box.querySelector('[data-solea-upsell-status]');
    if (status) status.textContent = message || '';
  };

  /**
   * Prefer the theme's own component so the cart re-renders the way Horizon
   * expects. Fall back to a plain request plus reload only if it is missing.
   */
  const setQuantity = async (button, target) => {
    setBusy(button, true, '');

    const box = button.closest('[data-solea-upsell]');
    track('quantity', {
      from_bags: parseInt(box?.dataset.bags || '0', 10),
      to_bags: target,
      unlocks_free_shipping: target >= 2,
    });

    const component = document.querySelector('cart-items-component');
    if (component && typeof component.updateQuantity === 'function') {
      component.updateQuantity({ line: 1, quantity: target, action: 'change' });
      return;
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: 1, quantity: target }),
      });
      if (!response.ok) throw new Error('cart change failed');
      if (!(await refreshCart())) window.location.reload();
      else reopenCartDrawer();
    } catch (error) {
      setBusy(button, false, "That didn't work — please use the quantity selector above.");
    }
  };

  const subscribe = async (button) => {
    const box = button.closest('[data-solea-upsell]');
    const variant = box?.dataset.variant;
    const bags = parseInt(box?.dataset.bags || '0', 10);
    const packQty = parseInt(box?.dataset.packQty || '3', 10);
    // Plan IDs arrive as data attributes: an inline script would not survive the
    // cart being re-rendered, and the IDs must stay in step with the rendered cart.
    const planId = bags >= packQty ? box?.dataset.planPack : box?.dataset.planSingle;

    if (!variant || !planId) {
      setBusy(button, false, 'Subscription options are on the product page.');
      return;
    }

    track('subscribe', { bags, plan_id: planId });

    setBusy(button, true, 'Switching to subscription…');

    try {
      await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variant, quantity: bags || 1, selling_plan: planId }),
      });
      if (!response.ok) throw new Error('add failed');
      // Refresh in place so the drawer stays open. This is the fix for the mobile
      // report that switching to subscription closed the cart.
      if (!(await refreshCart())) {
        window.location.reload();
        return;
      }
      reopenCartDrawer();
      setBusy(button, false, '');
    } catch (error) {
      setBusy(button, false, "That didn't work — please choose Subscribe on the product page.");
    }
  };

  /**
   * Close the mobile navigation drawer when an in-page link is followed.
   *
   * The Solea nav is almost entirely same-page anchors (/#story, /#faq, ...). A
   * hash link does not unload the document, so Horizon's <details>-based drawer is
   * never torn down and stays open on top of the section the visitor just asked to
   * see. Cross-page links close it for free because the page navigates.
   *
   * Delegating to <header-drawer>'s own close() keeps focus trap, scroll lock and
   * the close animation consistent; the manual attribute strip is only a fallback
   * for when the custom element has not upgraded yet.
   */
  const closeMenuDrawer = (link) => {
    const drawer = link.closest('header-drawer');
    if (!drawer) return;

    try {
      if (typeof drawer.close === 'function') {
        drawer.close();
        return;
      }
    } catch (error) {
      /* fall through to the manual path */
    }

    drawer.querySelectorAll('details[open]').forEach((details) => {
      details.classList.remove('menu-open');
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  };

  // Capture phase: Horizon's cart components stop propagation on some clicks, so
  // bubbling alone is not reliable inside the drawer.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Mobile menu: close on same-page navigation. Runs before the cart checks
      // because a nav link is never an upsell button, and we must not preventDefault
      // here -- the browser still needs to perform the jump.
      const navLink = target.closest('.menu-drawer a[href], .menu-drawer__navigation a[href]');
      if (navLink) {
        const href = navLink.getAttribute('href') || '';
        const samePage =
          href.startsWith('#') ||
          href.startsWith('/#') ||
          href === '/' ||
          href === window.location.pathname ||
          href === `${window.location.pathname}${window.location.hash}`;
        // Defer so the browser starts the jump first; closing synchronously can
        // cancel the scroll in Safari.
        if (samePage) setTimeout(() => closeMenuDrawer(navLink), 60);
        return;
      }

      const qtyButton = target.closest('[data-solea-upsell-qty]');
      if (qtyButton) {
        event.preventDefault();
        const next = parseInt(qtyButton.getAttribute('data-solea-upsell-qty') || '0', 10);
        if (next > 0) setQuantity(qtyButton, next);
        return;
      }

      const subButton = target.closest('[data-solea-upsell-sub]');
      if (subButton) {
        event.preventDefault();
        subscribe(subButton);
      }
    },
    true
  );

  /**
   * Hook reconciliation to cart changes.
   *
   * Horizon dispatches its own cart events, but their names vary by version, so we
   * also watch the network layer. Patching fetch is heavy-handed yet reliable: it
   * catches the theme's quantity stepper, the trash icon, and any app that mutates
   * the cart, none of which we control.
   */
  const CART_MUTATIONS = /\/cart\/(change|add|update|clear)/;

  window.fetch = async function patchedFetch(...args) {
    const response = await nativeFetch.apply(this, args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      if (CART_MUTATIONS.test(url) && response.ok) {
        // Let the theme finish its own re-render first.
        setTimeout(reconcilePlans, 300);
      }
    } catch (error) {
      /* no-op */
    }
    return response;
  };

  // Also check on load, in case a cart was left in a mismatched state earlier.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(reconcilePlans, 500));
  } else {
    setTimeout(reconcilePlans, 500);
  }
})();
